import { fetchGetProfile } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

// Authentication state for the partner-branded-signup app. The signup flow
// serves both Cognito-authenticated iwoca staff (impersonating a customer)
// and Django-authenticated returning customers, so we have to detect either.
//
// Pattern adapted from credit-compass-frontend's useAuth hook.
//
// Tradeoff (sequential vs parallel probes): the /profile fallback is gated
// on `cognito.isError`, so the two requests run sequentially rather than in
// parallel. That saves a /profile call on the cognito-success path (which
// becomes the common case post-migration) at the cost of an extra
// round-trip of latency for django-only users mid-migration: their cognito
// probe must 401 before /profile starts. We accept the temporary latency
// cost during migration; once customers are fully migrated to Cognito the
// fallback becomes a no-op and the hook collapses to a single /auth/me
// call. If the latency hit becomes painful in practice, drop the
// `cognito.isError` guard to fire both in parallel.

type TCognitoMeResponse = {
  sub: string;
  email: string;
  [key: string]: unknown;
};

// Derive the auth-service host by swapping the leftmost label with "auth".
// www.iwoca.co.uk → auth.iwoca.co.uk, www.iwoca.de → auth.iwoca.de, and
// likewise for iwoca-dev.* — mirrors how auth-service resolves its cookie
// domain from the inbound Host header, so a single deployment serves both
// UK and DE on every environment. In dev, /auth is proxied via vite.
const getAuthBaseUrl = (): string => {
  if (import.meta.env.DEV) {
    return '/auth';
  }
  const host = window.location.host;
  const firstDot = host.indexOf('.');
  const authHost =
    firstDot !== -1 ? `auth.${host.substring(firstDot + 1)}` : host;
  return `https://${authHost}`;
};

const fetchCognitoMe = async (): Promise<TCognitoMeResponse> => {
  const response = await fetch(`${getAuthBaseUrl()}/me`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`auth /me ${response.status}`);
  }
  return response.json();
};

type TAuthState = {
  isLoggedIn: boolean;
  isLoading: boolean;
  authType: 'cognito' | 'django' | 'none';
};

/**
 * Resolves the current user's logged-in state.
 *
 * Probes both auth backends in parallel: the Cognito-backed /auth/me
 * endpoint on auth-service, and the LAPI /profile endpoint that uses the
 * legacy Django session. The user is logged in if either succeeds.
 *
 * Replaces the legacy `getCookie('isLoggedIn') === 'True'` pattern, which
 * relied on iwoca-django's IsLoggedInMiddleware stamping a cookie on every
 * response — that breaks for endpoints that bypass auth at the gateway.
 *
 * Pass `enabled: false` to skip the network calls entirely — useful for
 * code paths (e.g. direct-customer signup) that don't need auth gating.
 */
export const useAuth = ({
  enabled = true,
}: { enabled?: boolean } = {}): TAuthState => {
  const cognito = useQuery({
    queryKey: ['auth', 'cognitoMe'],
    queryFn: fetchCognitoMe,
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Only fall back to the Django /profile call when cognito has resolved
  // and failed — saves a request for the now-common cognito-success path.
  // Reuses `useGetProfile`'s query key so React Query dedupes the call
  // when a Django-authenticated user hits both hooks on the same page.
  const userProfile = useQuery({
    queryKey: ['fetchGetProfile'],
    queryFn: () => fetchGetProfile({}),
    enabled: enabled && cognito.isError,
    retry: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // When the caller has opted out, surface a stable logged-out state instead
  // of leaving consumers stuck on isLoading=true forever.
  if (!enabled) {
    return { isLoggedIn: false, isLoading: false, authType: 'none' };
  }

  if (cognito.isSuccess) {
    return { isLoggedIn: true, isLoading: false, authType: 'cognito' };
  }

  if (userProfile.isSuccess) {
    return { isLoggedIn: true, isLoading: false, authType: 'django' };
  }

  // Loading covers both the initial cognito probe and the django fallback
  // it triggers — only resolve to logged-out once both have failed.
  if (cognito.isPending || (cognito.isError && !userProfile.isError)) {
    return { isLoggedIn: false, isLoading: true, authType: 'none' };
  }

  return { isLoggedIn: false, isLoading: false, authType: 'none' };
};
